import apiClient from "../config/api.js";

export function initialize(store, router) {
    // Full logout function - defined at the top so it's available to all functions
    async function performFullLogout() {
        // Clear all authentication data from sessionStorage
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('userRole');
        sessionStorage.removeItem('userId');
        sessionStorage.removeItem('brigade');
        sessionStorage.removeItem('name');
        sessionStorage.removeItem('nameShort');
        sessionStorage.removeItem('baseUrl');

        // Clear Vuex store state
        if (store && store.dispatch) {
            await store.dispatch('logout');
        }

        // Reset API client default headers
        delete apiClient.defaults.headers.common["Authorization"];

        // Force a complete page reload to clear all component state
        window.location.href = '/';  // This causes a full page reload
    }

    // Function to validate token with backend
    async function validateTokenWithBackend(token, baseUrl) {
        if (!token || !baseUrl) {
            return false;
        }

        try {
            const response = await apiClient.get(`${baseUrl}/auth/validate-token`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Check both status and response body
            return response.data.status === 200;
        } catch (error) {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error("Token validation error:", error.response.data);
            } else if (error.request) {
                // The request was made but no response was received
                console.error("No response received:", error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error("Error setting up request:", error.message);
            }

            // Perform full logout
            await performFullLogout();
            return false;
        }
    }

    router.beforeEach(async (to, from, next) => {
        // Get user data from sessionStorage
        const token = sessionStorage.getItem('token');
        const baseUrl = sessionStorage.getItem('baseUrl');
        const userRole = sessionStorage.getItem('userRole');
        const userBrigade = sessionStorage.getItem('brigade');
        const userId = sessionStorage.getItem('userId');

        // Case 1: Route requires authentication
        if (to.matched.some(record => record.meta.requiresAuth)) {
            // If no token, redirect to login
            if (!token) {
                console.log("No token found, redirecting to login");
                return next({name: "login"});
            }

            // Validate token with backend
            const isTokenValid = await validateTokenWithBackend(token, baseUrl);

            // If token is invalid, performFullLogout has already been called
            // and the page will be reloaded, so we don't need to continue
            if (!isTokenValid) {
                return; // Don't proceed with navigation as page will reload
            }

            // Check if route requires admin privileges
            if (to.matched.some(record => record.meta.requiresAdmin)) {
                if (userRole !== 'ADMINISTRATOR') {
                    console.warn('Accès refusé : seuls les administrateurs peuvent accéder à cette page');
                    return next({name: 'login'}); // Redirect to login or a 403 page
                }
            }

            // Token is valid - handle meta.type routes with role-based redirection
            if (to.matched.some(record => record.meta.type)) {
                // Check role first
                if ((userRole === "ADMINISTRATOR") || (userRole === "RESPONSABLE MOYENS GÉNÉRAUX")) {
                    return next("/agents/liste_principale");
                } else if (userRole === "FRONT") {
                    return next("/cards/cmdCards");
                } else if (userRole === "TPE") {
                    return next("/pointages/infos_tpe");
                } else if (userRole === "RESTAURATEUR" || userRole === "POINT FOCAL RESTO") {
                    return next("/agents/commandes");
                }
                // Only check brigade and userId if none of the roles above match
                else if (userBrigade === "TSA-BRG-MFD" || userBrigade === "TSA-BRG-ANF"
                    || userId === "91804" || userId === "92204" || userId === "19001" || userId === "20052" || userId === "92208"
                    || userId === "22014" || userId === "19129" || userId === "18046" || userId === "19385" || userId === "18108"
                    || userId === "18004" || userId === "21029" || userId === "18075") {
                    return next("/agents/liste_principale");
                } else {
                    return next("/agents/commandesindividuelles");
                }
            } else {
                return next(); // Continue to requested route
            }
        }
        // Case 2: Route doesn't require auth, but we're trying to access login while logged in
        else if (to.name === 'login' && token) {
            // Validate the token before redirecting
            const isTokenValid = await validateTokenWithBackend(token, baseUrl);
            if (isTokenValid) {
                // Redirect based on role
                if ((userRole === "ADMINISTRATOR") ||(userRole === "RESPONSABLE MOYENS GÉNÉRAUX")) {
                    return next("/agents/liste_principale");
                } else if (userRole === "FRONT") {
                    return next("/cards/cmdCards");
                } else if (userRole === "TPE") {
                    return next("/pointages/infos_tpe");
                } else if (userRole === "RESTAURATEUR" || userRole === "POINT FOCAL RESTO") {
                    return next("/agents/commandes")
                }
                // Only check brigade and userId if none of the roles above match
                else if (userBrigade === "TSA-BRG-MFD" || userBrigade === "TSA-BRG-ANF"
                    || userId === "91804" || userId === "92204" || userId === "19001" || userId === "20052" || userId === "92208"
                    || userId === "22014" || userId === "19129" || userId === "18046" || userId === "19385" || userId === "18108"
                    || userId === "18004" || userId === "21029" || userId === "18075") {
                    return next("/agents/liste_principale");
                } else {
                    return next("/agents/commandesindividuelles");
                }
            }
            // If token is invalid, we'll have been logged out already
        }
        // Case 3: Public route, proceed normally
        else {
            return next();
        }
    });

    // Set authorization header if token exists
    const token = sessionStorage.getItem('token');
    if (token) {
        setAuthorization(token);
    }
}

export function setAuthorization(token) {
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}